import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Link, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getLotDetail, updateLot } from '../../../services/ProductionService';
import { formatLotNumber } from './lotUtils';

export default function LotDetailModal({ lotId, canEdit, onClose, onSelectLot, onOpenProduction, onUpdated }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ lot: '', expirationDate: '', quantity: '' });

  const loadDetail = (id, isCancelled = () => false) => {
    setLoading(true);
    return getLotDetail(id)
      .then((data) => { if (!isCancelled()) setDetail(data); })
      .catch((err) => console.error('Error fetching lot detail:', err))
      .finally(() => { if (!isCancelled()) setLoading(false); });
  };

  useEffect(() => {
    setEditing(false);
    setError('');
    if (!lotId) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setDetail(null);
    loadDetail(lotId, () => cancelled);
    return () => { cancelled = true; };
  }, [lotId]);

  const startEditing = () => {
    setForm({
      lot: detail.lot.lot,
      expirationDate: detail.lot.expirationDate ? dayjs(detail.lot.expirationDate).format('YYYY-MM-DD') : '',
      quantity: detail.lot.quantity
    });
    setError('');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateLot(lotId, {
        lot: form.lot,
        expiration_date: form.expirationDate ? dayjs(form.expirationDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : null,
        quantity: detail.lot.quantity
      });
      setEditing(false);
      await loadDetail(lotId);
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const lot = detail?.lot;

  return (
    <Dialog open={!!lotId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('lot_detail')}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box textAlign="center" p={2}><CircularProgress /></Box>}
        {lot && (
          <>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
              <Typography variant="h6">{lot.name} · {lot.lot}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('lot_entry')}: {dayjs(lot.entryDate).format('DD/MM/YYYY')}
              {lot.expirationDate ? ` · ${t('expiration')}: ${dayjs(lot.expirationDate).format('DD/MM/YYYY')}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('lot_origin')}: {detail.originProductionId ? (
                <Link component="button" variant="body2" onClick={() => onOpenProduction(detail.originProductionId)}>
                  {t('lot_origin_production', { id: detail.originProductionId })}
                </Link>
              ) : t('lot_origin_replenishment', { id: lot.replenishmentId })}
            </Typography>

            {canEdit && !editing && (
              <Button size="small" sx={{ mt: 1 }} onClick={startEditing}>{t('lot_edit')}</Button>
            )}
            {editing && (
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mt={2}>
                <TextField size="small" label={t('lot_label')} value={form.lot} onChange={(e) => setForm({ ...form, lot: e.target.value })} />
                <TextField
                  size="small" type="date" label={t('expiration')} InputLabelProps={{ shrink: true }}
                  value={form.expirationDate} onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                />
                <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>{t('save')}</Button>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving}>{t('cancel')}</Button>
                {error && <Typography variant="body2" color="error">{error}</Typography>}
              </Box>
            )}

            {detail.originProductionId && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>{t('lot_inputs')}</Typography>
                {detail.inputLots.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('lot_inputs_empty')}</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('lot_item')}</TableCell>
                        <TableCell>{t('lot_label')}</TableCell>
                        <TableCell>{t('expiration')}</TableCell>
                        <TableCell align="right">{t('quantity')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.inputLots.map((input) => (
                        <TableRow key={input.id}>
                          <TableCell>{input.name}</TableCell>
                          <TableCell>
                            <Link component="button" variant="body2" onClick={() => onSelectLot(input.id)}>
                              {input.lot}
                            </Link>
                          </TableCell>
                          <TableCell>{input.expirationDate ? dayjs(input.expirationDate).format('DD/MM/YYYY') : '—'}</TableCell>
                          <TableCell align="right">
                            {input.quantity == null ? t('lot_quantity_unknown') : formatLotNumber(input.quantity)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>{t('lot_used_in')}</Typography>
            {detail.usages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('lot_used_in_empty')}</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('lot_produced_item')}</TableCell>
                    <TableCell>{t('lot_label')}</TableCell>
                    <TableCell>{t('date')}</TableCell>
                    <TableCell align="right">{t('quantity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.usages.map((usage) => (
                    <TableRow key={usage.productionId}>
                      <TableCell>
                        <Link component="button" variant="body2" onClick={() => onOpenProduction(usage.productionId)}>
                          {usage.productionId}
                        </Link>
                      </TableCell>
                      <TableCell>{usage.producedName}</TableCell>
                      <TableCell>{usage.productionLotNumber || '—'}</TableCell>
                      <TableCell>{dayjs(usage.productionDate).format('DD/MM/YYYY')}</TableCell>
                      <TableCell align="right">
                        {usage.quantity == null ? t('lot_quantity_unknown') : formatLotNumber(usage.quantity)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>{t('lot_produced_with')}</Typography>
            {detail.producedLots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('lot_produced_with_empty')}</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('lot_item')}</TableCell>
                    <TableCell>{t('lot_label')}</TableCell>
                    <TableCell align="right">{t('quantity')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.producedLots.map((produced) => (
                    <TableRow key={produced.id}>
                      <TableCell>{produced.name}</TableCell>
                      <TableCell>
                        <Link component="button" variant="body2" onClick={() => onSelectLot(produced.id)}>
                          {produced.lot}
                        </Link>
                      </TableCell>
                      <TableCell align="right">{formatLotNumber(produced.quantity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
